import { action, computed, makeObservable, observable } from 'mobx';

import { ErrorPopupConfig, GameErrorPayload, ModalState } from '../models';
import { ErrorPopupConfigs, ErrorPrefix } from '../constants';
import { jackpotSocket, launcherSocket } from '../api';
import { GameErrorCodes } from '../enums';
import { RootStore } from '.';

export class ModalStore {

  private rootStore: RootStore;

  public popupFeatureOpen = true;

  public gameInterruptionModalState: ModalState<ErrorPopupConfig | null> = { isOpen: false, data: null };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      popupFeatureOpen: observable,
      setPopupFeatureOpen: action.bound,

      gameInterruptionModalState: observable,
      setGameInterruptionModalState: action.bound,

      anyFeaturePopupOpen: computed
    });
  }

  public get anyFeaturePopupOpen(): boolean {
    const { tournamentStore, freeDropStore, boostedJackpotStore, cashbackStore, gameStore, freeSpinStore, sidebarMenuStore } = this.rootStore;
    return (
      sidebarMenuStore.isSidebarOpen ||
      !!freeSpinStore.visibleFreeSpin ||
      freeDropStore.showAnouncementPopup ||
      cashbackStore.announcementModalOpen ||
      tournamentStore.tournamentFrameOpen ||
      tournamentStore.tournamentHasClaimModalOpen ||
      (!!gameStore.launchData?.data?.popUps?.length && this.popupFeatureOpen) ||
      (tournamentStore.tournamentInfoModalOpen && tournamentStore.showTournamentInfo) ||
      (!!boostedJackpotStore.wheelInfo && boostedJackpotStore.isBoostedJackpotInitialPopup)
    );
  }

  private generateErrorPopupDynamicData(errorCode: GameErrorCodes, errorPayload?: GameErrorPayload): Partial<ErrorPopupConfig> {
    const result: Partial<ErrorPopupConfig> = { displayErrorCode: '' };
    const operatorErrorCode = errorPayload?.operatorErrorCode;
    const validationType = errorPayload?.validationType;
    const launcherFrontErrorCode = errorPayload?.launcherFrontErrorCode;
    if (errorCode === GameErrorCodes.OperatorError) {
      const errorMessage = errorPayload?.errorMessage;
      return {
        displayErrorCode: operatorErrorCode ? `${operatorErrorCode}` : '',
        message: errorMessage
      };
    }
    const { launchData: { data } } = this.rootStore.gameStore;
    if (!data) return result;
    const { category } = data;
    if (launcherFrontErrorCode || validationType || validationType === 0) {
      const errorCodePrefix = launcherFrontErrorCode ? ErrorPrefix.Platform : (ErrorPrefix[category as keyof typeof ErrorPrefix] || '');
      result.displayErrorCode = `${errorCodePrefix}0000${launcherFrontErrorCode || validationType}`;
    };
    return result;
  }

  public setPopupFeatureOpen(value: boolean): void {
    this.popupFeatureOpen = value;
  }

  public setGameInterruptionModalState(data: ModalState<ErrorPopupConfig | null>): void {
    this.gameInterruptionModalState = data;
  }

  public openLostConnectionModal(errorPayload?: GameErrorPayload): void {
    const data = ErrorPopupConfigs[GameErrorCodes.ConnectionError] as ErrorPopupConfig;
    if (
      this.gameInterruptionModalState.isOpen && (
        this.gameInterruptionModalState.data?.errorCode === GameErrorCodes.GameIsOpen ||
        this.gameInterruptionModalState.data?.errorCode === GameErrorCodes.SessionExpired ||
        this.gameInterruptionModalState.data?.errorCode === GameErrorCodes.ConnectionError
      )) return;
    this.setGameInterruptionModalState({
      isOpen: true,
      data: {
        ...data,
        ...this.generateErrorPopupDynamicData(GameErrorCodes.ConnectionError, errorPayload)
      }
    });
  }

  public openSessionTerminatedModal(errorPayload?: GameErrorPayload): void {
    if (!!this.rootStore.gameStore.launchData?.data?.isError) return;
    if (
      this.gameInterruptionModalState.isOpen && (
        this.gameInterruptionModalState.data?.errorCode === GameErrorCodes.GameIsOpen ||
        this.gameInterruptionModalState.data?.errorCode === GameErrorCodes.SessionExpired ||
        this.gameInterruptionModalState.data?.errorCode === GameErrorCodes.ConnectionError
      )) return;
    const data = ErrorPopupConfigs[GameErrorCodes.SessionExpired] as ErrorPopupConfig;
    this.setGameInterruptionModalState({
      isOpen: true,
      data: {
        ...data,
        ...this.generateErrorPopupDynamicData(GameErrorCodes.SessionExpired, errorPayload)
      }
    });
    this.rootStore.postMessageStore.sendStopGame();
    launcherSocket.stop();
    jackpotSocket.stop();
  }

  public openGameIsOpenedModal(errorPayload?: GameErrorPayload): void {
    const data = ErrorPopupConfigs[GameErrorCodes.GameIsOpen] as ErrorPopupConfig;
    this.setGameInterruptionModalState({
      isOpen: true,
      data: {
        ...data,
        ...this.generateErrorPopupDynamicData(GameErrorCodes.GameIsOpen, errorPayload)
      }
    });
    this.rootStore.postMessageStore.sendStopGame();
    launcherSocket.stop();
    jackpotSocket.stop();
  }

  public openNotEnoughMoneyModal(errorPayload?: GameErrorPayload): void {
    const data = ErrorPopupConfigs[GameErrorCodes.NotEnoughMoney] as ErrorPopupConfig;
    this.setGameInterruptionModalState({
      isOpen: true,
      data: {
        ...data,
        ...this.generateErrorPopupDynamicData(GameErrorCodes.NotEnoughMoney, errorPayload)
      }
    });
  }

  public openPlayerLimitExceededModal(errorPayload?: GameErrorPayload): void {
    const data = ErrorPopupConfigs[GameErrorCodes.PlayerLimitExceeded] as ErrorPopupConfig;
    this.setGameInterruptionModalState({
      isOpen: true,
      data: {
        ...data,
        ...this.generateErrorPopupDynamicData(GameErrorCodes.PlayerLimitExceeded, errorPayload)
      }
    });
  }

  public openGeneralErrorModal(errorPayload?: GameErrorPayload): void {
    const data = ErrorPopupConfigs[GameErrorCodes.GeneralError] as ErrorPopupConfig;
    this.setGameInterruptionModalState({
      isOpen: true,
      data: {
        ...data,
        ...this.generateErrorPopupDynamicData(GameErrorCodes.GeneralError, errorPayload)
      }
    });
  }

  public openOperatorErrorModal(errorPayload?: GameErrorPayload): void {
    const data = ErrorPopupConfigs[GameErrorCodes.OperatorError] as ErrorPopupConfig;
    this.setGameInterruptionModalState({
      isOpen: true,
      data: {
        ...data,
        ...this.generateErrorPopupDynamicData(GameErrorCodes.OperatorError, errorPayload)
      }
    });
  }

  public closeGameInterruptionModal(): void {
    this.setGameInterruptionModalState({ isOpen: false, data: null });
  }

}
